import { Injectable } from '@angular/core';
import { RestService } from 'cargo-chief-commons';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { UrlService } from '@service/url/url.service';

export interface AllocationDTO {
	id?: string;
	allocationId?: string; // API may return this instead of 'id'
	projectId: string;
	carrierId: string;
	allocationType: string;
	allocationValue: number;
	allocationPeriod: string;
	allocationWindowMinutes: number;
	isActive: boolean;
	// CN-8662: optional effective window (YYYY-MM-DD). Outside it the allocation is skipped.
	effectiveStartDate?: string;
	effectiveEndDate?: string;
	createdAt?: Date;
	updatedAt?: Date;
}

export interface AllocationStatusDTO {
	allocationId: string;
	projectId: string;
	carrierId: string;
	carrierName?: string;
	carrierMC?: string;

	// Configuration
	allocationType: string;
	allocationPeriod: string;
	allocationValue: number;
	expectedVolume?: number;
	priority?: number;
	allocationWindowMinutes: number;
	enabled: boolean;

	// Current period status
	periodStart?: Date;
	periodEnd?: Date;
	allocatedCount?: number;
	acceptedCount?: number;
	rejectedCount?: number;
	expiredCount?: number;
	totalLoadsInPeriod?: number;
	targetCount?: number;
	remainingCapacity?: number;
	actualPercentage?: number;
	hasCapacity?: boolean;

	// Timestamps
	created?: Date;
	lastModified?: Date;
}

@Injectable({
	providedIn: 'root',
})
export class AllocationService {
	constructor(
		private restService: RestService,
		private urlService: UrlService
	) {}

	/**
	 * Get allocation for a specific project-carrier combination
	 * Returns null for 404 (allocation doesn't exist), throws for other errors
	 */
	getAllocation(projectId: string, carrierId: string): Observable<AllocationDTO | null> {
		const url = this.urlService.getAllocationUrl(projectId, carrierId);
		return this.restService.GET(url).pipe(
			map((response: any) => response?.data || null),
			catchError((error) => {
				if (error.status === 404) {
					return of(null); // Legitimately doesn't exist
				}
				console.error('Failed to get allocation:', error);
				throw error; // Network or other errors should propagate
			})
		);
	}

	/**
	 * Create a new allocation
	 */
	createAllocation(allocation: AllocationDTO): Observable<AllocationDTO | null> {
		const url = this.urlService.createAllocationUrl();
		return this.restService.POST(url, allocation).pipe(
			map((response: any) => response?.data || response),
			catchError((error) => {
				console.error('Failed to create allocation:', error);
				throw error;
			})
		);
	}

	/**
	 * Update an existing allocation
	 */
	updateAllocation(allocationId: string, allocation: Partial<AllocationDTO>): Observable<AllocationDTO | null> {
		const url = this.urlService.updateAllocationUrl(allocationId);
		return this.restService.PUT(url, allocation).pipe(
			map((response: any) => response?.data || response),
			catchError((error) => {
				console.error('Failed to update allocation:', error);
				throw error;
			})
		);
	}

	/**
	 * Get allocation status (usage statistics)
	 * Returns null for 404 (allocation doesn't exist), throws for other errors
	 */
	getAllocationStatus(allocationId: string): Observable<AllocationStatusDTO | null> {
		const url = this.urlService.getAllocationStatusUrl(allocationId);
		return this.restService.GET(url).pipe(
			map((response: any) => response?.data || null),
			catchError((error) => {
				if (error.status === 404) {
					return of(null); // Legitimately doesn't exist
				}
				console.error('Failed to get allocation status:', error);
				throw error; // Network or other errors should propagate
			})
		);
	}

	/**
	 * Get all allocations for a project
	 * Returns an empty array if no allocations exist
	 */
	getAllocationsForProject(projectId: string): Observable<AllocationStatusDTO[]> {
		const url = this.urlService.getAllocationsForProjectUrl(projectId);
		return this.restService.GET(url).pipe(
			map((response: any) => response?.data || response || []),
			catchError((error) => {
				if (error.status === 404) {
					return of([]); // No allocations exist for this project
				}
				console.error('Failed to get allocations for project:', error);
				return of([]); // Return empty array on error to avoid breaking the UI
			})
		);
	}

	/**
	 * Delete an allocation
	 */
	deleteAllocation(allocationId: string): Observable<void> {
		const url = this.urlService.deleteAllocationUrl(allocationId);
		return this.restService.DELETE(url).pipe(
			map(() => undefined),
			catchError((error) => {
				console.error('Failed to delete allocation:', error);
				throw error;
			})
		);
	}
}
